import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Loader2, MessageSquare, Plus, ArrowRight, Send, ArrowLeft, Sparkles } from 'lucide-react';
import { MessageTicks } from './MessageTicks';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatTimestamp = (value) => {
  if (!value) return 'No messages yet';
  try {
    return new Date(value).toLocaleString('en-GB');
  } catch (error) {
    return value;
  }
};

const getTenancyLabel = (tenancy) => {
  const tenants = Array.isArray(tenancy?.tenants) ? tenancy.tenants : [];
  const participantNames = tenants
    .map((tenant) => tenant?.name || tenant?.email)
    .filter(Boolean);

  if (participantNames.length === 0) {
    return tenancy?.id || 'Unnamed tenancy';
  }

  return participantNames.join(', ');
};

export const MessageCenterContent = ({ embedded = false, initialThreadId = null, onUnreadCountChange }) => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingTenantThread, setCreatingTenantThread] = useState(false);

  const [composeOpen, setComposeOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [propertyTenancies, setPropertyTenancies] = useState([]);
  const [loadingPropertyTenancies, setLoadingPropertyTenancies] = useState(false);
  const [selectedTenancyId, setSelectedTenancyId] = useState('');
  const [creatingLandlordThread, setCreatingLandlordThread] = useState(false);

  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [messageBody, setMessageBody] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [suggestingMessage, setSuggestingMessage] = useState(false);

  const fetchThreads = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/messages/threads`, {
        withCredentials: true,
      });
      const nextThreads = response.data || [];
      setThreads(nextThreads);
      if (typeof onUnreadCountChange === 'function') {
        const unreadTotal = nextThreads.reduce(
          (sum, thread) => sum + (thread?.unread_count || 0),
          0,
        );
        onUnreadCountChange(unreadTotal);
      }
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [onUnreadCountChange, user?.id]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  useEffect(() => {
    if (embedded && initialThreadId) {
      setSelectedThreadId(initialThreadId);
    }
  }, [embedded, initialThreadId]);

  const fetchThreadDetail = useCallback(async (threadId) => {
    if (!threadId || !user?.id) return;

    setThreadLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/messages/threads/${threadId}`, {
        withCredentials: true,
      });

      setSelectedThread(response.data?.thread || null);
      setThreadMessages(response.data?.messages || []);

      await axios.put(`${API_URL}/api/messages/threads/${threadId}/read`, {}, {
        withCredentials: true,
      });

      await fetchThreads();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load thread');
    } finally {
      setThreadLoading(false);
    }
  }, [fetchThreads, user?.id]);

  useEffect(() => {
    if (!embedded || !selectedThreadId) return;
    fetchThreadDetail(selectedThreadId);
  }, [embedded, selectedThreadId, fetchThreadDetail]);

  const fetchLandlordProperties = useCallback(async () => {
    if (user?.role !== 'landlord') return;

    setLoadingProperties(true);
    try {
      const response = await axios.get(`${API_URL}/api/properties`, {
        withCredentials: true,
      });
      setProperties(response.data || []);
    } catch (error) {
      toast.error('Failed to load properties for compose');
    } finally {
      setLoadingProperties(false);
    }
  }, [user?.role]);

  useEffect(() => {
    if (composeOpen && user?.role === 'landlord' && properties.length === 0) {
      fetchLandlordProperties();
    }
  }, [composeOpen, user?.role, properties.length, fetchLandlordProperties]);

  const fetchPropertyTenancies = useCallback(async (propertyId) => {
    if (!propertyId || user?.role !== 'landlord') return;

    setLoadingPropertyTenancies(true);
    try {
      const response = await axios.get(`${API_URL}/api/tenancies/property/${propertyId}`, {
        withCredentials: true,
      });
      const nextTenancies = (response.data?.tenancies || []).filter((tenancy) => {
        const tenants = Array.isArray(tenancy?.tenants) ? tenancy.tenants : [];
        return !tenancy?.is_manual && tenants.some((tenant) => !tenant?.is_manual && tenant?.email);
      });
      setPropertyTenancies(nextTenancies);
    } catch (error) {
      setPropertyTenancies([]);
      toast.error('Failed to load tenancies for selected property');
    } finally {
      setLoadingPropertyTenancies(false);
    }
  }, [user?.role]);

  const handleSelectProperty = async (propertyId) => {
    setSelectedPropertyId(propertyId);
    setSelectedTenancyId('');
    await fetchPropertyTenancies(propertyId);
  };

  const handleStartTenantConversation = async () => {
    setCreatingTenantThread(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/messages/threads`,
        {},
        { withCredentials: true }
      );
      const threadId = response?.data?.id;
      if (!threadId) {
        throw new Error('Thread id missing from response');
      }
      if (embedded) {
        setSelectedThreadId(threadId);
        await fetchThreads();
      } else {
        navigate(`/messages/${threadId}`);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Unable to start conversation');
    } finally {
      setCreatingTenantThread(false);
    }
  };

  const handleStartLandlordConversation = async () => {
    if (!selectedTenancyId || !selectedPropertyId) {
      toast.error('Please select a property and tenancy');
      return;
    }

    setCreatingLandlordThread(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/messages/threads`,
        {
          tenancy_id: selectedTenancyId,
        },
        { withCredentials: true }
      );

      const threadId = response?.data?.id;
      if (!threadId) {
        throw new Error('Thread id missing from response');
      }

      setComposeOpen(false);
      if (embedded) {
        setSelectedThreadId(threadId);
        await fetchThreads();
      } else {
        navigate(`/messages/${threadId}`);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Unable to start conversation');
    } finally {
      setCreatingLandlordThread(false);
    }
  };

  const handleOpenThread = (threadId) => {
    if (embedded) {
      setSelectedThreadId(threadId);
      return;
    }
    navigate(`/messages/${threadId}`);
  };

  const handleSuggestMessage = async () => {
    if (!selectedThreadId) return;
    setSuggestingMessage(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/messages/threads/${selectedThreadId}/suggest-reply`,
        {},
        { withCredentials: true },
      );
      setMessageBody(response.data.suggestion || '');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not generate a suggestion');
    } finally {
      setSuggestingMessage(false);
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    const trimmed = messageBody.trim();
    if (!trimmed || !selectedThreadId) return;

    setSendingMessage(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/messages/threads/${selectedThreadId}`,
        { body: trimmed },
        { withCredentials: true },
      );
      setThreadMessages((prev) => [...prev, response.data]);
      setMessageBody('');
      await fetchThreads();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[30vh]">
        <Loader2 className="h-8 w-8 animate-spin text-royal-600" />
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-8' : 'max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-8'} data-testid="message-center-page">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-heading text-3xl font-bold text-navy-900">Message Center</h1>
          <p className="text-muted-foreground mt-1">Direct messages between landlords and tenants.</p>
        </div>

        {user?.role === 'tenant' && (
          <Button className="flex-shrink-0" onClick={handleStartTenantConversation} disabled={creatingTenantThread}>
            {creatingTenantThread ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Start conversation
          </Button>
        )}

        {user?.role === 'landlord' && (
          <div className="flex-shrink-0">
          <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Compose message
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Compose Message</DialogTitle>
                <DialogDescription>Select property and tenancy to open a conversation thread.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium mb-2">Property</p>
                  <Select value={selectedPropertyId} onValueChange={handleSelectProperty}>
                    <SelectTrigger>
                      <SelectValue placeholder={loadingProperties ? 'Loading properties...' : 'Select property'} />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((property) => (
                        <SelectItem key={property.id} value={property.id}>
                          {property.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">Tenancy</p>
                  <Select
                    value={selectedTenancyId}
                    onValueChange={setSelectedTenancyId}
                    disabled={!selectedPropertyId || loadingPropertyTenancies}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingPropertyTenancies ? 'Loading tenancies...' : 'Select tenancy'} />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTenancies.map((tenancy) => (
                        <SelectItem key={tenancy.id} value={tenancy.id}>
                          {getTenancyLabel(tenancy)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  className="w-full"
                  onClick={handleStartLandlordConversation}
                  disabled={!selectedPropertyId || !selectedTenancyId || creatingLandlordThread}
                >
                  {creatingLandlordThread ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  Open conversation
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {threads.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-muted-foreground" />
              No threads yet
            </CardTitle>
            <CardDescription>
              Messages for active tenancies appear here. Application conversations are shown in Applications.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className={embedded && selectedThreadId ? 'grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]' : 'space-y-4'}>
          <div className="space-y-4">
          {threads.map((thread) => (
            <Card
              key={thread.id}
              className={`cursor-pointer hover:border-royal-300 transition-colors ${selectedThreadId === thread.id ? 'border-royal-500' : ''}`}
              onClick={() => handleOpenThread(thread.id)}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{thread?.counterpart?.name || 'Unknown user'}</p>
                    <p className="text-sm text-muted-foreground truncate">
                      {thread?.last_message?.body || 'No messages yet'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {thread?.property?.title || 'Property'} • {formatTimestamp(thread?.last_message_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {thread?.unread_count > 0 && (
                      <Badge className="bg-royal-100 text-royal-700">{thread.unread_count}</Badge>
                    )}
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>

          {embedded && selectedThreadId && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2">
                  <span className="truncate">{selectedThread?.counterpart?.name || 'Conversation'}</span>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedThreadId(null)}>
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                </CardTitle>
                <CardDescription>{selectedThread?.property?.title || 'Property thread'}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="max-h-[42vh] overflow-y-auto space-y-4">
                  {threadLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-royal-600" />
                    </div>
                  ) : threadMessages.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No messages yet.</p>
                  ) : (
                    threadMessages.map((message) => {
                      const isOwn = message.sender_id === user?.id;
                      return (
                        <div key={message.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] rounded-xl px-4 py-3 ${isOwn ? 'bg-royal-600 text-white' : 'bg-muted'}`}>
                            <p className="text-sm whitespace-pre-wrap">{message.body}</p>
                            <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                              <p className={`text-[11px] ${isOwn ? 'text-royal-100' : 'text-muted-foreground'}`}>
                                {formatTimestamp(message.created_at)}
                              </p>
                              {isOwn && <MessageTicks isRead={message.is_read} />}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <form onSubmit={handleSendMessage} className="space-y-3">
                  <Textarea
                    value={messageBody}
                    onChange={(e) => setMessageBody(e.target.value)}
                    placeholder="Write your message..."
                    rows={3}
                    maxLength={5000}
                  />
                  <div className="flex items-center justify-between gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSuggestMessage}
                      disabled={suggestingMessage || sendingMessage}
                    >
                      {suggestingMessage
                        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                      Suggest reply
                    </Button>
                    <Button type="submit" disabled={sendingMessage || messageBody.trim().length === 0}>
                      {sendingMessage ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Send message
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
